import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VotingDeck from '../components/VotingDeck';
import HistoryPanel from '../components/HistoryPanel';
import ParticipantsPanel from '../components/room/ParticipantsPanel';
import StartVotingForm from '../components/room/StartVotingForm';
import VoteResults from '../components/room/VoteResults';
import StoryQueuePanel from '../components/room/StoryQueuePanel';
import { roomApi, votingApi } from '../services/api';
import { accountApi, authStore } from '../services/accountApi';
import { User, Room as RoomType, VotingSession, QueueItem } from '../types';
import { VotingHistoryItem } from '../types/history';
import { saveVotingResult } from '../utils/votingHistory';
import { isNotFoundError, apiErrorMessage } from '../utils/apiError';
import { averageVote, calculateVoteStats } from '../utils/voteStats';
import { loadRoomIdentity, clearRoomIdentity } from '../utils/roomIdentity';
import useRoomPolling from '../hooks/useRoomPolling';
import './Room.css';

const Room: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [room, setRoom] = useState<RoomType | null>(null);
  const [participants, setParticipants] = useState<User[]>([]);
  const [currentSession, setCurrentSession] = useState<VotingSession | null>(null);
  const [votes, setVotes] = useState<any[]>([]);
  const [selectedVote, setSelectedVote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'voting' | 'history'>('voting');
  const [flashMessage, setFlashMessage] = useState<string | null>(null);
  const [isRevealing, setIsRevealing] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [sessionEndInProgress, setSessionEndInProgressState] = useState<boolean>(false);

  // 終了処理中フラグはポーリングのawait後にも同期的に読む必要があるため、
  // 再描画用のstateと同時にrefでも保持する
  const sessionEndRef = useRef<boolean>(false);
  const setSessionEndInProgress = useCallback((value: boolean) => {
    sessionEndRef.current = value;
    setSessionEndInProgressState(value);
  }, []);

  // 終了直後のセッションがバックエンド反映前に返ってきた場合に無視するためのID
  const lastEndedSessionId = useRef<string | null>(null);

  // ポーリングのたびに新しい配列で再描画しないよう、内容が変わった時だけ差し替える
  const updateParticipantsIfChanged = useCallback((newParticipants: User[]) => {
    setParticipants(prevParticipants => {
      if (prevParticipants.length !== newParticipants.length) {
        return newParticipants;
      }

      const prevIds = prevParticipants.map(p => p.id).sort();
      const newIds = newParticipants.map(p => p.id).sort();
      if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
        return newParticipants;
      }

      const hasNameChanges = newParticipants.some(newP => {
        const prevP = prevParticipants.find(p => p.id === newP.id);
        return !prevP || prevP.display_name !== newP.display_name;
      });

      return hasNameChanges ? newParticipants : prevParticipants;
    });
  }, []);

  // room自体もポーリングで更新する（モデレーター委譲、ストーリーキューなど他の参加者の操作を反映するため）。
  // 変化がない限り同じ参照を保ち、不要な再描画を避ける
  const updateRoomIfChanged = useCallback((newRoom: RoomType) => {
    setRoom(prev => {
      if (!prev) return newRoom;
      if (
        prev.creator_id === newRoom.creator_id &&
        JSON.stringify(prev.story_queue || []) === JSON.stringify(newRoom.story_queue || [])
      ) {
        return prev;
      }
      return newRoom;
    });
  }, []);

  // フラッシュメッセージを自動的に消す
  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [flashMessage]);

  // ローカルストレージからこのルーム用のユーザー情報を取得
  useEffect(() => {
    if (!roomId) return;

    const identity = loadRoomIdentity(roomId);
    if (identity) {
      setUserId(identity.userId);
      setDisplayName(identity.displayName);
    } else {
      // ユーザー情報がない場合は、ホームに戻る
      // フルリロードではなくSPA内遷移で、バンドルの再取得・再実行を避ける
      navigate('/');
    }
  }, [roomId, navigate]);

  // ルームが消えていた場合はこのルームのローカル情報を捨ててホームへ戻す
  const handleRoomGone = useCallback(() => {
    if (roomId) clearRoomIdentity(roomId);
    navigate('/');
  }, [roomId, navigate]);

  // 自分の意思でルームから退出する
  const handleLeaveRoom = useCallback(async () => {
    if (!roomId || !userId) return;

    try {
      await roomApi.leaveRoom(roomId, userId);
    } catch (err) {
      console.error('Failed to leave room:', err);
      // 退出APIが失敗してもローカルには留まらず、ホームへは戻す
    }

    clearRoomIdentity(roomId);
    navigate('/');
  }, [roomId, userId, navigate]);

  // モデレーター権限を別の参加者に委譲する
  const handleMakeModerator = useCallback(
    async (newModeratorId: string) => {
      if (!roomId) return;

      try {
        const updatedRoom = await roomApi.changeModerator(roomId, newModeratorId);
        setRoom(updatedRoom);
      } catch (err) {
        console.error('Failed to change moderator:', err);
        setError('モデレーターの変更に失敗しました');
      }
    },
    [roomId]
  );

  // アクティブセッションの取得結果を状態へ反映する
  const applyActiveSession = useCallback(
    (session: VotingSession, sessionVotes: any[]) => {
      setCurrentSession(session);
      setVotes(sessionVotes);

      const myVote = sessionVotes.find(v => v.user_id === userId);
      if (myVote) {
        setSelectedVote(myVote.value);
      }

      // 公開が反映されたら公開中フラグを解除する
      if (session.is_revealed) {
        setIsRevealing(false);
      }
    },
    [userId]
  );

  const clearSessionState = useCallback(() => {
    setCurrentSession(null);
    setVotes([]);
    setSelectedVote(null);
  }, []);

  const loadRoomData = useCallback(async () => {
    if (!roomId) return;

    try {
      setLoading(true);
      const snapshot = await roomApi.getSnapshot(roomId);
      setRoom(snapshot.room);
      updateParticipantsIfChanged(snapshot.participants || []);

      if (snapshot.active_session) {
        applyActiveSession(snapshot.active_session.session, snapshot.active_session.votes || []);
      }
    } catch (err: any) {
      if (isNotFoundError(err)) {
        handleRoomGone();
        return;
      }
      console.error('Failed to load room data:', err);
      setError('ルームの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [roomId, updateParticipantsIfChanged, applyActiveSession, handleRoomGone]);

  // ポーリング1回分の処理。最新のstateを参照できるよう毎レンダー作り直す
  // room本体とアクティブセッションはsnapshot APIで1回のリクエストにまとめて取得する
  const pollRoomState = useCallback(async () => {
    if (!roomId || sessionEndRef.current) return;

    try {
      const snapshot = await roomApi.getSnapshot(roomId);
      // await中にセッション終了処理が始まっていたら反映しない
      if (sessionEndRef.current) return;

      updateRoomIfChanged(snapshot.room);
      updateParticipantsIfChanged(snapshot.participants || []);

      if (snapshot.active_session) {
        // 終了済みセッションがバックエンド反映前に返ってきた場合は無視する
        if (snapshot.active_session.session.id === lastEndedSessionId.current) return;
        applyActiveSession(snapshot.active_session.session, snapshot.active_session.votes || []);
      } else {
        clearSessionState();
      }
    } catch (err: any) {
      if (isNotFoundError(err)) {
        handleRoomGone();
      } else {
        console.error('Polling failed to fetch room snapshot:', err);
      }
    }
  }, [
    roomId,
    updateRoomIfChanged,
    updateParticipantsIfChanged,
    applyActiveSession,
    clearSessionState,
    handleRoomGone,
  ]);

  const polling = useRoomPolling({
    enabled: Boolean(roomId && userId),
    onPoll: pollRoomState,
  });

  useEffect(() => {
    if (roomId && userId) {
      loadRoomData();
    }
    // loadRoomDataはroomId/userId由来のみで変わるため、初回ロードとして一度だけ実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, userId]);

  const handleStartVoting = async (storyTitle: string, storyDescription: string) => {
    if (!roomId) return;

    try {
      const session = await votingApi.createSession(roomId, {
        story_title: storyTitle,
        story_description: storyDescription,
      });
      setCurrentSession(session);
      setVotes([]);
      setSelectedVote(null);
      setIsRevealing(false);

      // 新しいセッション開始時は過去の終了セッション情報をクリア
      lastEndedSessionId.current = null;
    } catch (err) {
      console.error('Failed to start voting:', err);
      setError('投票セッションの開始に失敗しました');
    }
  };

  // ストーリーキューの追加/削除はStoryQueuePanelがAPIを呼び、結果の配列だけ受け取って反映する
  const handleQueueChange = useCallback((queue: QueueItem[]) => {
    setRoom(prev => (prev ? { ...prev, story_queue: queue } : prev));
  }, []);

  // キューの項目でそのまま投票を開始する（キューからは削除してから開始する）
  const handleStartFromQueue = async (item: QueueItem) => {
    if (!roomId) return;

    try {
      const updatedQueue = await roomApi.removeFromQueue(roomId, item.id);
      handleQueueChange(updatedQueue);
    } catch (err) {
      console.error('Failed to remove queue item before starting:', err);
      // 削除に失敗してもキューに残るだけなので投票開始は続行する
    }

    await handleStartVoting(item.title, item.description || '');
  };

  const handleVoteSelect = async (value: string) => {
    if (!currentSession || !roomId) return;
    
    try {
      await votingApi.castVote(currentSession.id, {
        user_id: userId,
        value: value
      });
      setSelectedVote(value);
      
      // 即座にローカル投票状態を更新
      const newVote = {
        id: Date.now().toString(),
        user_id: userId,
        value: value,
        voted_at: new Date().toISOString(),
        user: { id: userId, display_name: displayName, joined_at: '' }
      };
      
      setVotes(prevVotes => {
        const existingIndex = prevVotes.findIndex(v => v.user_id === userId);
        if (existingIndex >= 0) {
          const updated = [...prevVotes];
          updated[existingIndex] = newVote;
          return updated;
        } else {
          return [...prevVotes, newVote];
        }
      });
    } catch (err) {
      console.error('Failed to cast vote:', err);
      setError('投票に失敗しました');
    }
  };

  const handleVoteRemove = async () => {
    if (!currentSession) return;

    try {
      await votingApi.removeVote(currentSession.id, userId);
      setSelectedVote(null);
      setVotes(prevVotes => prevVotes.filter(v => v.user_id !== userId));
    } catch (err) {
      console.error('Failed to remove vote:', err);
      setError('投票の取り消しに失敗しました');
    }
  };

  const handleRevealVotes = useCallback(async () => {
    if (!currentSession || isRevealing) return;

    setIsRevealing(true);

    try {
      await votingApi.revealVotes(currentSession.id);
      // 正常時はポーリングがis_revealedを検知した時点でフラグが解除される
    } catch (err) {
      console.error('Failed to reveal votes:', err);
      setError('投票結果の公開に失敗しました');
      setIsRevealing(false);
    }
  }, [currentSession, isRevealing]);

  // モデレーターが全投票をクリアして未公開に戻す（誤投票の修正にも、公開後の再投票にも使う）
  const handleResetVotes = useCallback(async () => {
    if (!currentSession || isResetting) return;

    setIsResetting(true);

    try {
      await votingApi.resetVotes(currentSession.id);
      setVotes([]);
      setSelectedVote(null);
      setIsRevealing(false);
      setCurrentSession(prev => (prev ? { ...prev, is_revealed: false } : prev));
    } catch (err) {
      console.error('Failed to reset votes:', err);
      setError('投票のリセットに失敗しました');
    } finally {
      setIsResetting(false);
    }
  }, [currentSession, isResetting]);

  // チームへの結果記録（ルーム作成時にチームを選択し、ログイン中の場合のみ）
  const recordResultToTeam = async (session: VotingSession, sessionVotes: any[], roomData: RoomType) => {
    if (!roomData.team_id || !authStore.isLoggedIn()) return;

    try {
      await accountApi.recordResult(roomData.team_id, {
        room_id: roomData.id,
        session_id: session.id,
        story_title: session.story_title,
        story_description: session.story_description,
        votes: sessionVotes.map((v) => ({
          display_name:
            participants.find((p) => p.id === v.user_id)?.display_name || v.user_id,
          value: v.value,
        })),
      });
      console.log('Result recorded to team:', roomData.team_id);
    } catch (err: any) {
      // 重複記録(409)は正常扱い。それ以外も投票フローは妨げない
      if (err.response?.status !== 409) {
        console.error('Failed to record result to team:', err);
      }
    }
  };

  // 履歴保存のヘルパー関数
  const saveVotingResultToHistory = (session: VotingSession, sessionVotes: any[], roomData: RoomType) => {
    try {
      const averagePoint = averageVote(sessionVotes);

      // 最終的な合意ポイントを決定（平均値を使用、数値でない場合は最初の投票）
      const finalPoint = averagePoint !== undefined
        ? averagePoint.toFixed(1)
        : sessionVotes[0]?.value || sessionVotes[0]?.point || '?';

      const historyItem: VotingHistoryItem = {
        id: `${session.id}-${Date.now()}`,
        storyTitle: session.story_title || 'Untitled Story',
        roomName: roomData.name,
        roomId: roomData.id,
        finalPoint,
        averagePoint,
        participants: participants.map(p => p.display_name),
        votedAt: new Date().toISOString(),
        votes: sessionVotes.map(vote => ({
          participant: participants.find(p => p.id === vote.user_id)?.display_name || vote.user_id,
          point: vote.value || vote.point || '?'
        }))
      };

      saveVotingResult(historyItem);
      console.log('✅ Voting result saved to history:', historyItem);
    } catch (error) {
      console.error('❌ Error saving voting result to history:', error);
    }
  };

  const handleNextVotingSession = async () => {
    if (!roomId || !currentSession) return;

    const endingSession = currentSession;

    // 終了処理中はポーリングによる状態の巻き戻しを防ぐ
    polling.pause();
    setSessionEndInProgress(true);

    try {
      await roomApi.endActiveSession(roomId);
    } catch (err: any) {
      // 既に終了済み(404)なら成功として扱う
      if (!isNotFoundError(err)) {
        console.error('Failed to end session:', err);
        setError(`セッションの終了に失敗しました: ${apiErrorMessage(err, 'Unknown error')}`);
        setSessionEndInProgress(false);
        polling.resume();
        return;
      }
    }

    // 終了済みセッションが再取得されても無視できるようIDを控える
    lastEndedSessionId.current = endingSession.id;

    if (votes.length > 0 && room) {
      saveVotingResultToHistory(endingSession, votes, room);
      // チームが設定されていればバックエンドにも記録する
      recordResultToTeam(endingSession, votes, room);
    }

    clearSessionState();
    setError('');
    setIsRevealing(false);
    setSessionEndInProgress(false);
    setFlashMessage('セッションを終了しました。新しい投票を開始できます。');

    polling.resume();
  };

  const isUserModerator = () => {
    return room && room.creator_id === userId;
  };

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback: 古いブラウザや非セキュアコンテキスト対応
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        return true;
      } catch {
        return false;
      }
    }
  };

  const getVoteStatus = () => {
    if (!currentSession || !participants || !votes) return null;
    
    const totalParticipants = participants.length;
    const totalVotes = votes.length;
    
    return {
      voted: totalVotes,
      total: totalParticipants,
      percentage: totalParticipants > 0 ? Math.round((totalVotes / totalParticipants) * 100) : 0,
    };
  };


  const votedUserIds = useMemo(() => new Set(votes.map((v) => v.user_id)), [votes]);

  // 参加者リストを安定化（ソート順を固定）
  const sortedParticipants = useMemo(() => {
    if (!participants || participants.length === 0) return [];
    
    return [...participants].sort((a, b) => {
      // モデレーターを最初に表示
      if (room) {
        if (a.id === room.creator_id && b.id !== room.creator_id) return -1;
        if (b.id === room.creator_id && a.id !== room.creator_id) return 1;
      }
      
      // その後は表示名でソート
      return a.display_name.localeCompare(b.display_name);
    });
  }, [participants, room]);

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  if (error) {
    return (
      <div className="error">
        <p>{error}</p>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="error">
        <p>ルームが見つかりません</p>
      </div>
    );
  }

  const voteStatus = getVoteStatus();
  const stats = calculateVoteStats(votes);

  return (
    <div className="room-container">
      {/* フラッシュメッセージ */}
      {flashMessage && (
        <div className="flash-message">
          {flashMessage}
        </div>
      )}
      
      <div className="room-header">
        <div className="header-content">
          <div className="room-info">
            <h1>{room.name}</h1>
            <div className="room-id-container">
              <p className="room-id-label">ルームID:</p>
              <div className="room-id-input-group">
                <input
                  type="text"
                  value={roomId}
                  readOnly
                  className="room-id-input"
                />
                <button
                  onClick={async () => {
                    if (!roomId) return;
                    const copied = await copyTextToClipboard(roomId);
                    setFlashMessage(copied ? 'ルームIDをコピーしました！' : 'コピーに失敗しました');
                  }}
                  className="copy-button"
                >
                  コピー
                </button>
              </div>
            </div>
          </div>
          <div className="room-tabs">
            <button 
              className={`tab-button ${activeTab === 'voting' ? 'active' : ''}`}
              onClick={() => setActiveTab('voting')}
            >
              投票
            </button>
            <button 
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              履歴
            </button>
          </div>
        </div>
      </div>

      <div className="room-content">
        {activeTab === 'voting' && (
          <>
            <ParticipantsPanel
              participants={sortedParticipants}
              moderatorId={room.creator_id}
              votedUserIds={votedUserIds}
              showVoteStatus={Boolean(currentSession && !currentSession.is_revealed)}
              currentUserId={userId}
              isModerator={Boolean(isUserModerator())}
              onLeave={handleLeaveRoom}
              onMakeModerator={handleMakeModerator}
            />

            {!currentSession && isUserModerator() && (
              <StartVotingForm onStart={handleStartVoting} />
            )}

            {!currentSession && !isUserModerator() && (
              <div className="waiting-panel card">
                <h3>投票待機中</h3>
                <p>モデレーターが新しい投票セッションを開始するのを待っています...</p>
              </div>
            )}

            {!currentSession && roomId && (
              <StoryQueuePanel
                roomId={roomId}
                queue={room.story_queue || []}
                isModerator={Boolean(isUserModerator())}
                onQueueChange={handleQueueChange}
                onStart={handleStartFromQueue}
              />
            )}

            {currentSession && (
              <div className="current-session card">
                <h3>現在の投票セッション</h3>
                <div className="session-info">
                  <h4>{currentSession.story_title}</h4>
                  {currentSession.story_description && (
                    <p className="session-description">{currentSession.story_description}</p>
                  )}
                </div>

                {voteStatus && (
                  <div className="vote-progress">
                    <p>投票状況: {voteStatus.voted}/{voteStatus.total} ({voteStatus.percentage}%)</p>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${voteStatus.percentage}%` }}
                      />
                    </div>
                  </div>
                )}

                {!currentSession.is_revealed && (
                  <VotingDeck
                    deckType={room.deck_type}
                    onVoteSelect={handleVoteSelect}
                    onVoteRemove={handleVoteRemove}
                    selectedValue={selectedVote || undefined}
                    disabled={false}
                  />
                )}

                {!currentSession.is_revealed && isUserModerator() && (
                  <div className="moderator-controls">
                    <button
                      onClick={handleRevealVotes}
                      className="btn btn-primary"
                      disabled={votes.length === 0 || isRevealing}
                    >
                      {isRevealing ? '公開中...' : '結果を公開'}
                    </button>
                    {votes.length > 0 && (
                      <button
                        onClick={handleResetVotes}
                        className="btn btn-secondary"
                        disabled={isResetting}
                      >
                        {isResetting ? 'クリア中...' : '投票をクリア'}
                      </button>
                    )}
                  </div>
                )}

                {currentSession.is_revealed && (
                  <VoteResults
                    session={currentSession}
                    votes={votes}
                    participants={sortedParticipants}
                    moderatorId={room.creator_id}
                    stats={stats}
                    onNextSession={isUserModerator() ? handleNextVotingSession : undefined}
                    onResetVotes={isUserModerator() ? handleResetVotes : undefined}
                    isEndingSession={sessionEndInProgress}
                    isResetting={isResetting}
                  />
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'history' && roomId && (
          <HistoryPanel roomId={roomId} participants={sortedParticipants} />
        )}
      </div>
    </div>
  );
};

export default Room;